import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { change, Field } from 'redux-form';
import { Alert, ButtonGroup, ButtonToolbar, Collapse } from 'reactstrap';
import { v4 as uuid } from 'uuid';
import { format } from 'date-fns';
import { mdiCalendarRange } from '@mdi/js';
import Icon from '@mdi/react';
import SmartButton from '../../../shared/components/SmartButton';
import renderField from '../../../shared/components/form/RenderField';
import Modal from '../../../shared/components/Modal';

const CalendarCycles = (props) => {
  const {
    t,
    showDialog,
    dispatch,
    dto,
    calendarCycles,
    onChangeCalendarCycles,
    onChangeCalendarDays,
    calendarDays,
  } = props;

  const [cycleList, setCycleList] = useState([]);
  const [quantity, setQuantity] = useState();
  const [modal, setModal] = useState(false);

  const toggleModal = () => {
    setModal(!modal);
  };

  const removeAllCyclesInCalendar = (updatedCalendarDaysMarkers) => {
    updatedCalendarDaysMarkers.forEach((calendarDay) => {
      const markerIndex = calendarDay.markers.findIndex(
        (marker) => marker.cycle
      );
      if (markerIndex > -1) {
        calendarDay.markers.splice(markerIndex, 1);
      }
    });
  };

  const generateCycles = () => {
    if (modal) {
      toggleModal();
    }
    const updatedCycles = [...new Array(quantity)].map((cycle, index) => ({
      id: 0,
      name: `${index + 1}º ${t('general.cycle')}`,
      sequence: index + 1,
      beginInMilli: null,
      endInMilli: null,
      uuid: calendarCycles.uuid || uuid(),
      color: '#767CD4',
    }));

    const updatedCalendarDaysMarkers = [...calendarDays];
    removeAllCyclesInCalendar(updatedCalendarDaysMarkers);
    onChangeCalendarDays(updatedCalendarDaysMarkers);
    setCycleList(updatedCycles);
    onChangeCalendarCycles(updatedCycles);
    dispatch(change('AcademicCalendarForm', 'calendarCycles', updatedCycles));
  };

  const generate = () => {
    if (quantity > 0) {
      if (cycleList.length) {
        toggleModal();
      } else {
        generateCycles();
      }
    }
  };

  const cyclesListRender = () => {
    return (
      <>
        {cycleList.length
          ? cycleList.map((cycle) => (
              <div
                className="w-100 d-flex mb-2 flex-column"
                key={`${cycle.name}-${cycle.id}`}
              >
                <div className="d-flex w-100 justify-content-between">
                  <div className="calendar-btn d-flex overflow-hidden text-nowrap text-ellipsis font-weight-bold">
                    {cycle.name}
                  </div>
                </div>
                <div className="d-flex p-0">
                  <Icon path={mdiCalendarRange} size={0.8} color="#6a2b8b" />
                  <button
                    type="button"
                    className="calendar-btn d-flex overflow-hidden text-nowrap text-ellipsis"
                    onClick={() => showDialog(cycle)}
                  >
                    {cycle.beginInMilli && cycle.endInMilli ? (
                      <>
                        <span className="pl-3">
                          {format(cycle.beginInMilli, 'dd/MM/yyyy')}
                        </span>
                        <span className="pl-3">
                          {t('general.conjunctive-voiceover')}
                        </span>
                        <span className="pl-3">
                          {format(cycle.endInMilli, 'dd/MM/yyyy')}
                        </span>
                      </>
                    ) : (
                      <span>{t('general.click-to-set')}</span>
                    )}
                  </button>
                </div>
              </div>
            ))
          : null}
      </>
    );
  };

  useEffect(() => {
    if (dto.stepQuantity !== quantity) {
      setQuantity(dto.stepQuantity);
    }
    setCycleList(calendarCycles);
  }, [dto]);

  useEffect(() => {
    setCycleList(calendarCycles);
  }, [calendarCycles]);

  return (
    <>
      <Modal
        color="warning"
        title={t('notifications.attention')}
        btn="Warning"
        message={t('notifications.generate-again')}
        toggle={toggleModal}
        modal={modal}
        onConfirm={generateCycles}
      />
      <div className="d-flex flex-column w-100">
        <div className="form__form-group-field mb-3">
          <Field
            name="stepQuantity"
            type="number"
            min="1"
            max="99"
            component={renderField}
            placeholder={t('general.placeholders.quantity')}
            onChange={({ target }) => setQuantity(parseInt(target.value, 10))}
            readOnly={dto.id > 0}
          />
        </div>
        <Field
          name="calendarCycles"
          component={(field) => (
            <Collapse
              isOpen={field.meta.touched && !!field.meta.error}
              className="width100 mb-2"
            >
              <Alert className="text-center mb-0" color="danger">
                {field.meta.error}
              </Alert>
            </Collapse>
          )}
        />
        {dto.id === 0 && (
          <ButtonToolbar className="m-0">
            <ButtonGroup className="btn-group--justified">
              <SmartButton
                color="success"
                label={t('general.generate-cycles')}
                onClick={generate}
              />
            </ButtonGroup>
          </ButtonToolbar>
        )}
        {cyclesListRender()}
      </div>
    </>
  );
};

CalendarCycles.propTypes = {
  t: PropTypes.func.isRequired,
  showDialog: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  onChangeCalendarCycles: PropTypes.func.isRequired,
  dto: PropTypes.shape(PropTypes.shape).isRequired,
  calendarCycles: PropTypes.arrayOf(PropTypes.shape(PropTypes.shape))
    .isRequired,
  onChangeCalendarDays: PropTypes.func.isRequired,
  calendarDays: PropTypes.arrayOf(PropTypes.shape(PropTypes.shape)).isRequired,
};

export default withTranslation('common')(CalendarCycles);
